// hero.tsx (FULLY FINAL & RESPONSIVE - Revised and Fixed)
import React from 'react';
import { CheckCircle } from 'lucide-react';

// Define the expected properties for the Hero component
interface HeroProps {
  bannerImageUrl: string; // unused but kept as-is
  videoUrl: string;
}

const Hero: React.FC<HeroProps> = ({ videoUrl }) => {

  // Key features list
  const keyFeatures = [
    { text: 'Enhances soil structure and water retention.', icon: CheckCircle },
    { text: '100% Organic, chemical-free, and natural.', icon: CheckCircle },
    { text: 'Boosts plant immunity and yield capacity.', icon: CheckCircle },
  ];

  return (
    // Updated padding/spacing for better mobile/desktop differentiation
    <section className="w-full bg-green-900 flex items-center justify-center min-h-[500px] relative overflow-hidden">

      {/* Main Container */}
      <div className="container mx-auto grid lg:grid-cols-2 gap-10 items-center relative z-10">

        {/* 1. LEFT SIDE: TEXT CONTENT */}
        <div className="flex flex-col justify-center p-4 sm:p-6 lg:p-0 text-center lg:text-left">

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight mb-6 font-serif">
            <span className="text-white">Crafting </span>
            <span className="text-orange-400">High-Performance </span>
            <span className="text-white">Vermi Compost</span>
          </h1>

          <p className="text-base sm:text-lg text-green-200 mb-8 max-w-lg mx-auto lg:mx-0">
            Our pure, organic compost is the foundational block for sustainable and fruitful farming.
          </p>

          {/* Feature List */}
          <ul className="space-y-3 max-w-lg mx-auto lg:mx-0">
            {keyFeatures.map((feature, index) => (
              <li key={index} className="flex items-start space-x-3 justify-center lg:justify-start">
                <feature.icon size={20} className="text-orange-400 mt-1 flex-shrink-0" />
                <span className="text-white text-base sm:text-lg">
                  {feature.text}
                </span>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <div className="mt-10 flex justify-center lg:justify-start">
            <a
              href="/study"
              className="bg-orange-500 text-white font-semibold py-3 px-8 rounded-lg hover:bg-orange-600 transition duration-300 shadow-xl text-base sm:text-lg"
            >
              DISCOVER OUR PRODUCTS
            </a>
          </div>
        </div>
        
        {/* 2. MOBILE VIDEO CONTAINER (Visible below large screens) */}
        <div className="lg:hidden w-full mt-8 flex justify-center">
          <div className="w-[85%] max-w-xs sm:max-w-sm rounded-xl overflow-hidden shadow-2xl aspect-video">
            <video
              src={videoUrl}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
        </div>

      </div>
      
      {/* 3. DESKTOP VIDEO (Semi-Circular - Visible on large screens and above) */}
      <div 
        // Removed fixed w-[700px] for better scaling, relied on lg/xl sizes
        className="hidden lg:flex absolute top-1/2 -translate-y-1/2 lg:w-[700px] lg:h-[700px] xl:w-[800px] xl:h-[800px] 
                   overflow-hidden items-center justify-start pointer-events-none"
        style={{ 
          borderRadius: '50% 0 0 50%', 
          // FIX: Used fixed negative 'right' value for stable off-canvas positioning.
          right: '-150px', 
          zIndex: 0, 
        }}
      >
        <video
          src={videoUrl}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          style={{
            transform: 'scaleX(1.5)',
          }}
        >
          Your browser does not support the video tag.
        </video>
      </div>

    </section>
  );
};

export default Hero;